/**
 * PresetReviews Component
 * Displays reviews for a preset with rating distribution and review list
 */

import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import {
  Star,
  ThumbsUp,
  MessageSquare,
  ChevronDown,
  User,
  CheckCircle,
  ShieldCheck,
  Loader2,
  Send,
  X,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Star rating display component
const StarRating = ({ rating, size = 'sm', interactive = false, onRate }) => {
  const [hoverRating, setHoverRating] = useState(0);
  const sizeClasses = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={!interactive}
          className={`${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'} transition-transform`}
          onMouseEnter={() => interactive && setHoverRating(star)}
          onMouseLeave={() => interactive && setHoverRating(0)}
          onClick={() => interactive && onRate?.(star)}
        >
          <Star
            className={`${sizeClasses[size]} ${
              star <= (hoverRating || rating)
                ? 'fill-amber-400 text-amber-400'
                : 'fill-zinc-600 text-zinc-600'
            } transition-colors`}
          />
        </button>
      ))}
    </div>
  );
};

// Rating distribution bar
const RatingBar = ({ stars, count, total }) => {
  const percentage = total > 0 ? (count / total) * 100 : 0;
  
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-4 text-zinc-400">{stars}</span>
      <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
      <div className="flex-1 h-2 bg-zinc-700 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.5, delay: stars * 0.1 }}
          className="h-full bg-amber-400 rounded-full"
        />
      </div>
      <span className="w-8 text-right text-zinc-500">{count}</span>
    </div>
  );
};

// Single review card
const ReviewCard = ({ review, onMarkHelpful, currentUserId }) => {
  const [isHelpful, setIsHelpful] = useState(false);
  const formattedDate = new Date(review.created_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

  const handleHelpful = async () => {
    if (isHelpful || review.user_id === currentUserId) return;
    
    try {
      await onMarkHelpful(review.id);
      setIsHelpful(true);
      toast.success('Marked as helpful');
    } catch (error) {
      toast.error('Failed to mark as helpful');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 bg-zinc-800/30 rounded-xl border border-zinc-700/50"
    >
      {/* Review header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center">
            <User className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-zinc-200">{review.user_name}</span>
              {review.verified_purchase && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/10 text-green-400 text-xs rounded-full border border-green-500/20">
                  <ShieldCheck className="w-3 h-3" />
                  Verified Purchase
                </span>
              )}
            </div>
            <span className="text-xs text-zinc-500">{formattedDate}</span>
          </div>
        </div>
        <StarRating rating={review.rating} size="sm" />
      </div>

      {/* Review content */}
      {review.title && (
        <h4 className="font-semibold text-zinc-200 mb-2">{review.title}</h4>
      )}
      {review.review_text && (
        <p className="text-zinc-400 text-sm leading-relaxed mb-3">{review.review_text}</p>
      )}

      {/* Review actions */}
      <div className="flex items-center justify-between pt-3 border-t border-zinc-700/50">
        <button
          onClick={handleHelpful}
          disabled={isHelpful || review.user_id === currentUserId}
          className={`flex items-center gap-2 text-sm transition-colors ${
            isHelpful
              ? 'text-green-400'
              : review.user_id === currentUserId
              ? 'text-zinc-600 cursor-not-allowed'
              : 'text-zinc-500 hover:text-zinc-300'
          }`}
        >
          <ThumbsUp className={`w-4 h-4 ${isHelpful ? 'fill-green-400' : ''}`} />
          <span>Helpful ({review.helpful_count + (isHelpful ? 1 : 0)})</span>
        </button>
      </div>
    </motion.div>
  );
};

// Write review form
const WriteReviewForm = ({ presetId, onSubmit, onCancel }) => {
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [reviewText, setReviewText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit({ rating, title, review_text: reviewText });
      toast.success('Review submitted successfully!');
    } catch (error) {
      toast.error(error.message || 'Failed to submit review');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.form
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      onSubmit={handleSubmit}
      className="p-4 bg-zinc-800/50 rounded-xl border border-zinc-700 mb-4"
    >
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-semibold text-zinc-200">Write a Review</h4>
        <button
          type="button"
          onClick={onCancel}
          className="p-1 text-zinc-500 hover:text-zinc-300 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Rating selector */}
      <div className="mb-4">
        <label className="block text-sm text-zinc-400 mb-2">Your Rating *</label>
        <StarRating rating={rating} size="lg" interactive onRate={setRating} />
      </div>

      {/* Title input */}
      <div className="mb-4">
        <label className="block text-sm text-zinc-400 mb-2">Review Title (optional)</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Summarize your experience"
          className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-zinc-200 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-violet-500"
          maxLength={100}
        />
      </div>

      {/* Review text */}
      <div className="mb-4">
        <label className="block text-sm text-zinc-400 mb-2">Your Review (optional)</label>
        <textarea
          value={reviewText}
          onChange={(e) => setReviewText(e.target.value)}
          placeholder="Share your experience with this preset..."
          rows={4}
          className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-zinc-200 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-violet-500 resize-none"
          maxLength={1000}
        />
        <p className="text-xs text-zinc-500 mt-1">{reviewText.length}/1000</p>
      </div>

      {/* Submit button */}
      <Button
        type="submit"
        disabled={isSubmitting || rating === 0}
        className="w-full bg-violet-600 hover:bg-violet-700"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Submitting...
          </>
        ) : (
          <>
            <Send className="w-4 h-4 mr-2" />
            Submit Review
          </>
        )}
      </Button>
    </motion.form>
  );
};

// Main PresetReviews component
const PresetReviews = ({ presetId, presetName, currentUserId }) => {
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({
    total: 0,
    average_rating: 0,
    rating_distribution: { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0 },
  });
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState('newest');
  const [showWriteReview, setShowWriteReview] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const fetchReviews = useCallback(async (pageNum = 1, append = false) => {
    try {
      const response = await fetch(
        `${API_URL}/api/guitar-presets/${presetId}/reviews?page=${pageNum}&limit=10&sort_by=${sortBy}`
      );
      if (!response.ok) throw new Error('Failed to fetch reviews');
      
      const data = await response.json();
      
      setReviews(prev => append ? [...prev, ...data.reviews] : data.reviews);
      setReviewStats({
        total: data.total,
        average_rating: data.average_rating,
        rating_distribution: data.rating_distribution,
      });
      setHasMore(data.reviews.length === 10);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setIsLoading(false);
    }
  }, [presetId, sortBy]);

  useEffect(() => {
    setPage(1);
    setIsLoading(true);
    fetchReviews(1, false);
  }, [fetchReviews]);

  const handleSubmitReview = async (reviewData) => {
    const response = await fetch(`${API_URL}/api/guitar-presets/${presetId}/review`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: currentUserId,
        ...reviewData,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to submit review');
    }

    setShowWriteReview(false);
    fetchReviews(1, false);
  };

  const handleMarkHelpful = async (reviewId) => {
    const response = await fetch(
      `${API_URL}/api/guitar-presets/${presetId}/reviews/${reviewId}/helpful?user_id=${currentUserId}`,
      { method: 'POST' }
    );
    if (!response.ok) throw new Error('Failed to mark as helpful');
  };

  const loadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchReviews(nextPage, true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-violet-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with rating summary */}
      <div className="flex flex-col md:flex-row md:items-start gap-6 p-4 bg-zinc-800/30 rounded-xl border border-zinc-700/50">
        {/* Average rating */}
        <div className="text-center md:text-left">
          <div className="text-5xl font-bold text-zinc-100 mb-1">
            {reviewStats.average_rating.toFixed(1)}
          </div>
          <StarRating rating={reviewStats.average_rating} size="md" />
          <p className="text-sm text-zinc-500 mt-1">
            {reviewStats.total} review{reviewStats.total !== 1 ? 's' : ''}
          </p>
        </div>

        {/* Rating distribution */}
        <div className="flex-1 space-y-1">
          {[5, 4, 3, 2, 1].map((stars) => (
            <RatingBar
              key={stars}
              stars={stars}
              count={reviewStats.rating_distribution[String(stars)] || 0}
              total={reviewStats.total}
            />
          ))}
        </div>

        {/* Write review button */}
        <div>
          <Button
            onClick={() => setShowWriteReview(true)}
            className="bg-violet-600 hover:bg-violet-700"
            disabled={!currentUserId}
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Write a Review
          </Button>
          {!currentUserId && (
            <p className="text-xs text-zinc-500 mt-2">Login to write a review</p>
          )}
        </div>
      </div>

      {/* Write review form */}
      <AnimatePresence>
        {showWriteReview && (
          <WriteReviewForm
            presetId={presetId}
            onSubmit={handleSubmitReview}
            onCancel={() => setShowWriteReview(false)}
          />
        )}
      </AnimatePresence>

      {/* Sort controls */}
      {reviews.length > 0 && (
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-zinc-200">
            Reviews ({reviewStats.total})
          </h3>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-zinc-300 focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="highest">Highest Rated</option>
            <option value="lowest">Lowest Rated</option>
            <option value="helpful">Most Helpful</option>
          </select>
        </div>
      )}

      {/* Reviews list */}
      <div className="space-y-4">
        {reviews.length > 0 ? (
          reviews.map((review) => (
            <ReviewCard
              key={review.id}
              review={review}
              onMarkHelpful={handleMarkHelpful}
              currentUserId={currentUserId}
            />
          ))
        ) : (
          <div className="text-center py-8 text-zinc-500">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No reviews yet. Be the first to review this preset!</p>
          </div>
        )}
      </div>

      {/* Load more button */}
      {hasMore && reviews.length > 0 && (
        <div className="text-center">
          <Button
            variant="outline"
            onClick={loadMore}
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            <ChevronDown className="w-4 h-4 mr-2" />
            Load More Reviews
          </Button>
        </div>
      )}
    </div>
  );
};

export default PresetReviews;
export { StarRating, ReviewCard, WriteReviewForm };
